import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, RefreshCw, Lock, Ban, Gauge, Clock, Snowflake, UserX, Download, Wallet, ArrowDownToLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SecurityTab — admin-only view of recent security events: rate-limit hits
 * (429s), blocked withdrawal attempts and login lockouts, plus the list of
 * currently-locked accounts. Read-only; data from /api/admin/security/overview.
 */
export default function SecurityTab({ language }) {
  const isLV = language === 'lv';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [acting, setActing] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [dossierUserId, setDossierUserId] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [dossierLoading, setDossierLoading] = useState(false);

  const load = async (d = days, t = typeFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = { days: d };
      if (t) params.event_type = t;
      const res = await axios.get(`${API}/admin/security/overview`, { params });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days, typeFilter); }, [days, typeFilter]);

  const downloadCsv = async () => {
    try {
      const params = { days };
      if (typeFilter) params.event_type = typeFilter;
      const resp = await axios.get(`${API}/admin/security/export.csv`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kolv-security-${days}d${typeFilter ? '-' + typeFilter : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const openDossier = async (userId) => {
    if (!userId) return;
    setDossierUserId(userId);
    setDossier(null);
    setDossierLoading(true);
    try {
      const res = await axios.get(`${API}/admin/security/users/${userId}/dossier`);
      setDossier(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
      setDossierUserId(null);
    } finally {
      setDossierLoading(false);
    }
  };

  const doAction = async (userId, action, label) => {
    if ((action === 'suspend' || action === 'unsuspend')
        && !window.confirm(isLV
          ? `Vai tiešām vēlies ${action === 'suspend' ? 'aizturēt' : 'atjaunot'} šo kontu?`
          : `Really ${action === 'suspend' ? 'suspend' : 'restore'} this account?`)) {
      return;
    }
    setActing(`${userId}:${action}`);
    try {
      await axios.post(`${API}/admin/security/users/${userId}/action`, { action });
      toast.success(label);
      await load(days, typeFilter);
      if (dossierUserId === userId) {
        const res = await axios.get(`${API}/admin/security/users/${userId}/dossier`);
        setDossier(res.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setActing(null);
    }
  };

  const typeMeta = {
    rate_limit: {
      label: isLV ? 'Ātruma limits (429)' : 'Rate limit (429)',
      icon: Gauge, color: 'text-[#D97736] bg-[#FDF3EC] border-[#F3D9C4]',
    },
    withdrawal_blocked: {
      label: isLV ? 'Bloķēta izmaksa' : 'Blocked withdrawal',
      icon: Ban, color: 'text-red-700 bg-red-50 border-red-200',
    },
    login_lockout: {
      label: isLV ? 'Konta bloķēšana' : 'Login lockout',
      icon: Lock, color: 'text-purple-700 bg-purple-50 border-purple-200',
    },
    withdrawals_auto_frozen: {
      label: isLV ? 'Auto-iesaldētas izmaksas' : 'Withdrawals auto-frozen',
      icon: Snowflake, color: 'text-sky-700 bg-sky-50 border-sky-200',
    },
    admin_action: {
      label: isLV ? 'Admin darbība' : 'Admin action',
      icon: ShieldAlert, color: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    },
  };

  const detailLabel = (d) => {
    const map = {
      insufficient_balance: isLV ? 'Nepietiekams atlikums' : 'Insufficient balance',
      race_guard: isLV ? 'Sacīkstes aizsardzība' : 'Race guard',
      aml_cap: isLV ? 'AML limits' : 'AML cap',
      frozen: isLV ? 'Iesaldēts' : 'Frozen',
      fraud_threshold: isLV ? 'Krāpniecības slieksnis' : 'Fraud threshold',
      account_locked: isLV ? 'Konts bloķēts' : 'Account locked',
      freeze_withdrawals: isLV ? 'Iesaldēt izmaksas' : 'Freeze withdrawals',
      unfreeze_withdrawals: isLV ? 'Atsaldēt izmaksas' : 'Unfreeze withdrawals',
      suspend: isLV ? 'Aizturēt kontu' : 'Suspend account',
      unsuspend: isLV ? 'Atjaunot kontu' : 'Restore account',
      withdraw: isLV ? 'Izmaksa' : 'Withdraw',
      parse: isLV ? 'Meklēšana' : 'Search',
      expand: isLV ? 'Sinonīmi' : 'Synonyms',
      suggest: isLV ? 'Sludin. palīgs' : 'Listing assistant',
    };
    return map[d] || d || '—';
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('lv-LV', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div data-testid="admin-security-tab" className="space-y-6">
      {/* Header + range selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#B91C1C] to-[#D97736] flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-outfit text-xl font-bold text-[#121212]">
              {isLV ? 'Drošība' : 'Security'}
            </h2>
            <p className="text-xs text-[#5C5C5C]">
              {isLV ? 'Nesenie drošības notikumi un bloķējumi' : 'Recent security events & blocks'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[1, 7, 30].map(opt => (
            <button
              key={opt}
              onClick={() => setDays(opt)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                days === opt
                  ? 'bg-[#D97736] text-white border-[#D97736]'
                  : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736]'
              }`}
              data-testid={`admin-security-range-${opt}d`}
            >
              {opt}d
            </button>
          ))}
          <Button
            type="button" size="sm" variant="outline" onClick={downloadCsv}
            disabled={loading || !data} className="h-8 gap-1.5 text-xs"
            data-testid="admin-security-export-csv"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </Button>
          <Button
            type="button" size="sm" variant="ghost" onClick={() => load(days, typeFilter)}
            disabled={loading} className="h-8 w-8 p-0"
            data-testid="admin-security-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600" data-testid="admin-security-error">{error}</p>}

      {loading && !data && (
        <div className="grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}
        </div>
      )}

      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI
              label={isLV ? 'Kopā notikumi' : 'Total events'}
              value={data.total_events.toLocaleString('lv-LV')}
              testid="admin-security-kpi-total"
            />
            <KPI
              label={isLV ? 'Ātruma limits' : 'Rate limits'}
              value={(data.by_type.rate_limit || 0).toLocaleString('lv-LV')}
              testid="admin-security-kpi-ratelimit"
            />
            <KPI
              label={isLV ? 'Bloķētas izmaksas' : 'Blocked withdrawals'}
              value={(data.by_type.withdrawal_blocked || 0).toLocaleString('lv-LV')}
              testid="admin-security-kpi-withdrawals"
            />
            <KPI
              label={isLV ? 'Auto-iesaldēts' : 'Auto-frozen'}
              value={(data.by_type.withdrawals_auto_frozen || 0).toLocaleString('lv-LV')}
              testid="admin-security-kpi-frozen"
            />
            <KPI
              label={isLV ? 'Konta bloķējumi' : 'Login lockouts'}
              value={(data.by_type.login_lockout || 0).toLocaleString('lv-LV')}
              testid="admin-security-kpi-lockouts"
            />
          </div>

          {/* Currently-locked accounts */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden" data-testid="admin-security-locked">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E4E0]">
              <Lock className="w-4 h-4 text-[#5C5C5C]" />
              <h3 className="font-medium text-[#121212]">
                {isLV ? 'Pašlaik bloķētie konti' : 'Currently locked accounts'}
              </h3>
              <Badge variant="outline" className="text-[10px] ml-1">{data.locked_accounts.length}</Badge>
            </div>
            {data.locked_accounts.length === 0 ? (
              <p className="px-5 py-5 text-sm text-[#5C5C5C]">
                {isLV ? 'Nav bloķētu kontu — viss kārtībā.' : 'No locked accounts — all clear.'}
              </p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E5E4E0] bg-[#FAFAFA]">
                    <th className="text-left px-5 py-2.5 text-xs font-medium text-[#5C5C5C] uppercase">E-pasts</th>
                    <th className="text-left px-5 py-2.5 text-xs font-medium text-[#5C5C5C] uppercase">IP</th>
                    <th className="text-right px-5 py-2.5 text-xs font-medium text-[#5C5C5C] uppercase">{isLV ? 'Mēģinājumi' : 'Attempts'}</th>
                    <th className="text-right px-5 py-2.5 text-xs font-medium text-[#5C5C5C] uppercase">{isLV ? 'Līdz' : 'Until'}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.locked_accounts.map((a, i) => (
                    <tr key={i} className="border-b border-[#E5E4E0] last:border-b-0" data-testid={`admin-security-locked-row-${i}`}>
                      <td className="px-5 py-2.5 text-sm text-[#121212]">{a.email || '—'}</td>
                      <td className="px-5 py-2.5 text-sm text-[#5C5C5C] font-mono">{a.ip || '—'}</td>
                      <td className="px-5 py-2.5 text-right text-sm font-medium text-[#121212]">{a.attempts}</td>
                      <td className="px-5 py-2.5 text-right text-xs text-[#5C5C5C]">{fmtTime(a.locked_until)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent events feed */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden" data-testid="admin-security-events">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E4E0] flex-wrap">
              <Clock className="w-4 h-4 text-[#5C5C5C]" />
              <h3 className="font-medium text-[#121212]">
                {isLV ? 'Nesenie notikumi' : 'Recent events'}
              </h3>
              <div className="flex items-center gap-1.5 ml-auto flex-wrap">
                {[
                  { v: '', label: isLV ? 'Visi' : 'All' },
                  { v: 'rate_limit', label: isLV ? 'Ātruma limits' : 'Rate limit' },
                  { v: 'withdrawal_blocked', label: isLV ? 'Bloķētas izmaksas' : 'Blocked' },
                  { v: 'withdrawals_auto_frozen', label: isLV ? 'Auto-iesaldēts' : 'Auto-frozen' },
                  { v: 'login_lockout', label: isLV ? 'Bloķēšana' : 'Lockout' },
                  { v: 'admin_action', label: isLV ? 'Admin' : 'Admin' },
                ].map(opt => (
                  <button
                    key={opt.v || 'all'}
                    onClick={() => setTypeFilter(opt.v)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                      typeFilter === opt.v
                        ? 'bg-[#121212] text-white border-[#121212]'
                        : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'
                    }`}
                    data-testid={`admin-security-filter-${opt.v || 'all'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {data.events.length === 0 ? (
              <p className="px-5 py-6 text-center text-sm text-[#5C5C5C]">
                {isLV ? 'Šajā periodā nav drošības notikumu.' : 'No security events in this period.'}
              </p>
            ) : (
              <div className="divide-y divide-[#E5E4E0]">
                {data.events.map((e, i) => {
                  const meta = typeMeta[e.event_type] || { label: e.event_type, icon: ShieldAlert, color: 'text-[#5C5C5C] bg-[#FAFAFA] border-[#E5E4E0]' };
                  const Icon = meta.icon;
                  const amount = e.meta?.amount;
                  const status = e.user_id ? data.user_status?.[e.user_id] : null;
                  const canManage = status && !status.is_admin;
                  const clickable = !!e.user_id;
                  return (
                    <div key={e.id} className="flex items-center gap-3 px-5 py-3 hover:bg-[#FAFAFA] transition-colors" data-testid={`admin-security-event-${i}`}>
                      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${meta.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <button
                        type="button"
                        onClick={() => clickable && openDossier(e.user_id)}
                        className={`min-w-0 flex-1 text-left ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`admin-security-event-open-${i}`}
                        disabled={!clickable}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-[#121212]">{meta.label}</span>
                          <Badge variant="outline" className="text-[10px]">{detailLabel(e.detail)}</Badge>
                          {amount != null && (
                            <span className="text-xs text-[#5C5C5C]">€{Number(amount).toFixed(2)}</span>
                          )}
                          {status?.withdrawals_frozen && (
                            <Badge className="text-[10px] bg-sky-100 text-sky-700 border-sky-200" data-testid={`admin-security-badge-frozen-${i}`}>
                              <Snowflake className="w-3 h-3 mr-1" />{isLV ? 'Izmaksas iesaldētas' : 'Withdrawals frozen'}
                            </Badge>
                          )}
                          {status?.is_suspended && (
                            <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200" data-testid={`admin-security-badge-suspended-${i}`}>
                              <UserX className="w-3 h-3 mr-1" />{isLV ? 'Konts aizturēts' : 'Suspended'}
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-[#5C5C5C] truncate">
                          {e.email || e.user_id || (e.meta?.key) || '—'}
                          {e.ip ? ` · ${e.ip}` : ''}
                          {clickable && <span className="text-[#D97736]"> · {isLV ? 'skatīt dosjē' : 'view dossier'}</span>}
                        </div>
                      </button>
                      {canManage && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            type="button" size="sm" variant="outline"
                            className="h-7 px-2 text-[11px] gap-1"
                            disabled={!!acting}
                            onClick={() => doAction(
                              e.user_id,
                              status.withdrawals_frozen ? 'unfreeze_withdrawals' : 'freeze_withdrawals',
                              status.withdrawals_frozen ? (isLV ? 'Izmaksas atsaldētas' : 'Withdrawals unfrozen') : (isLV ? 'Izmaksas iesaldētas' : 'Withdrawals frozen'),
                            )}
                            data-testid={`admin-security-freeze-${i}`}
                          >
                            <Snowflake className="w-3 h-3" />
                            {status.withdrawals_frozen ? (isLV ? 'Atsaldēt' : 'Unfreeze') : (isLV ? 'Iesaldēt' : 'Freeze')}
                          </Button>
                          <Button
                            type="button" size="sm" variant="outline"
                            className={`h-7 px-2 text-[11px] gap-1 ${status.is_suspended ? '' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                            disabled={!!acting}
                            onClick={() => doAction(
                              e.user_id,
                              status.is_suspended ? 'unsuspend' : 'suspend',
                              status.is_suspended ? (isLV ? 'Konts atjaunots' : 'Account restored') : (isLV ? 'Konts aizturēts' : 'Account suspended'),
                            )}
                            data-testid={`admin-security-suspend-${i}`}
                          >
                            <UserX className="w-3 h-3" />
                            {status.is_suspended ? (isLV ? 'Atjaunot' : 'Restore') : (isLV ? 'Aizturēt' : 'Suspend')}
                          </Button>
                        </div>
                      )}
                      <span className="text-[11px] text-[#5C5C5C] shrink-0">{fmtTime(e.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* User security dossier side panel */}
      <Sheet open={!!dossierUserId} onOpenChange={(o) => { if (!o) { setDossierUserId(null); setDossier(null); } }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="admin-security-dossier">
          <SheetHeader>
            <SheetTitle>{isLV ? 'Lietotāja drošības dosjē' : 'User security dossier'}</SheetTitle>
            <SheetDescription>
              {isLV ? 'Izmaksu vēsture, maka atlikums un visi drošības notikumi' : 'Withdrawal history, wallet balance and all security events'}
            </SheetDescription>
          </SheetHeader>

          {dossierLoading && (
            <div className="mt-6 space-y-3">
              {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-[#FAFAFA] rounded-xl animate-pulse" />)}
            </div>
          )}

          {dossier && !dossierLoading && (
            <div className="mt-5 space-y-5">
              {/* Identity + status */}
              <div className="rounded-2xl border border-[#E5E4E0] p-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#B91C1C] to-[#D97736] text-white font-semibold flex items-center justify-center">
                    {(dossier.user.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-[#121212] truncate">{dossier.user.name || '—'}</div>
                    <div className="text-xs text-[#5C5C5C] truncate">{dossier.user.email || '—'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-3">
                  <Badge variant="outline" className="text-[10px]">{dossier.user.role}</Badge>
                  {dossier.user.seller_type && <Badge variant="outline" className="text-[10px]">{dossier.user.seller_type}</Badge>}
                  {dossier.user.withdrawals_frozen && (
                    <Badge className="text-[10px] bg-sky-100 text-sky-700 border-sky-200"><Snowflake className="w-3 h-3 mr-1" />{isLV ? 'Izmaksas iesaldētas' : 'Frozen'}</Badge>
                  )}
                  {dossier.user.is_suspended && (
                    <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200"><UserX className="w-3 h-3 mr-1" />{isLV ? 'Aizturēts' : 'Suspended'}</Badge>
                  )}
                </div>
                {dossier.user.role !== 'admin' && (
                  <div className="flex items-center gap-2 mt-4">
                    <Button
                      type="button" size="sm" variant="outline"
                      className="flex-1 h-8 text-xs gap-1.5"
                      disabled={!!acting}
                      onClick={() => doAction(
                        dossier.user.id,
                        dossier.user.withdrawals_frozen ? 'unfreeze_withdrawals' : 'freeze_withdrawals',
                        dossier.user.withdrawals_frozen ? (isLV ? 'Izmaksas atsaldētas' : 'Withdrawals unfrozen') : (isLV ? 'Izmaksas iesaldētas' : 'Withdrawals frozen'),
                      )}
                      data-testid="dossier-freeze-btn"
                    >
                      <Snowflake className="w-3.5 h-3.5" />
                      {dossier.user.withdrawals_frozen ? (isLV ? 'Atsaldēt izmaksas' : 'Unfreeze') : (isLV ? 'Iesaldēt izmaksas' : 'Freeze withdrawals')}
                    </Button>
                    <Button
                      type="button" size="sm" variant="outline"
                      className={`flex-1 h-8 text-xs gap-1.5 ${dossier.user.is_suspended ? '' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                      disabled={!!acting}
                      onClick={() => doAction(
                        dossier.user.id,
                        dossier.user.is_suspended ? 'unsuspend' : 'suspend',
                        dossier.user.is_suspended ? (isLV ? 'Konts atjaunots' : 'Account restored') : (isLV ? 'Konts aizturēts' : 'Account suspended'),
                      )}
                      data-testid="dossier-suspend-btn"
                    >
                      <UserX className="w-3.5 h-3.5" />
                      {dossier.user.is_suspended ? (isLV ? 'Atjaunot kontu' : 'Restore') : (isLV ? 'Aizturēt kontu' : 'Suspend account')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Wallet + fraud pressure */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-[#E5E4E0] p-3" data-testid="dossier-balance">
                  <div className="text-[10px] text-[#5C5C5C] flex items-center gap-1"><Wallet className="w-3 h-3" />{isLV ? 'Atlikums' : 'Balance'}</div>
                  <div className="font-outfit text-lg font-bold text-[#121212]">€{Number(dossier.wallet.balance || 0).toFixed(2)}</div>
                </div>
                <div className="rounded-xl border border-[#E5E4E0] p-3">
                  <div className="text-[10px] text-[#5C5C5C]">{isLV ? 'Gaida' : 'Pending'}</div>
                  <div className="font-outfit text-lg font-bold text-[#121212]">€{Number(dossier.wallet.pending_balance || 0).toFixed(2)}</div>
                </div>
                <div className="rounded-xl border border-[#E5E4E0] p-3">
                  <div className="text-[10px] text-[#5C5C5C]">{isLV ? 'Bloķēts 24h' : 'Blocked 24h'}</div>
                  <div className={`font-outfit text-lg font-bold ${dossier.blocked_withdrawals_24h > 0 ? 'text-red-600' : 'text-[#121212]'}`}>{dossier.blocked_withdrawals_24h}</div>
                </div>
              </div>

              {/* 30-day activity timeline */}
              {dossier.timeline && (() => {
                const peak = dossier.timeline.reduce((m, d) => Math.max(m, d.withdrawals + d.blocked), 0);
                const totalWd = dossier.timeline.reduce((s, d) => s + d.withdrawals, 0);
                const totalBlk = dossier.timeline.reduce((s, d) => s + d.blocked, 0);
                return (
                  <div className="rounded-2xl border border-[#E5E4E0] p-4" data-testid="dossier-timeline">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-[#121212]">{isLV ? '30 dienu aktivitāte' : '30-day activity'}</h4>
                      <div className="flex items-center gap-3 text-[10px] text-[#5C5C5C]">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#D97736]" />{isLV ? 'Izmaksas' : 'Withdrawals'} {totalWd}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" />{isLV ? 'Bloķēts' : 'Blocked'} {totalBlk}</span>
                      </div>
                    </div>
                    {peak === 0 ? (
                      <p className="text-xs text-[#5C5C5C]">{isLV ? 'Nav aktivitātes pēdējās 30 dienās.' : 'No activity in the last 30 days.'}</p>
                    ) : (
                      <>
                        <div className="flex items-end gap-[2px] h-20">
                          {dossier.timeline.map(d => {
                            const total = d.withdrawals + d.blocked;
                            const hPct = peak ? Math.max(total ? 6 : 0, (total / peak) * 100) : 0;
                            const blkPct = total ? (d.blocked / total) * 100 : 0;
                            return (
                              <div
                                key={d.date}
                                className="flex-1 min-w-[3px] flex flex-col justify-end rounded-t overflow-hidden bg-[#F3EEE8]"
                                style={{ height: '100%' }}
                                title={`${d.date}: ${d.withdrawals} izmaksas, ${d.blocked} bloķēts`}
                                data-testid={`dossier-timeline-day-${d.date}`}
                              >
                                <div className="w-full flex flex-col justify-end" style={{ height: `${hPct}%` }}>
                                  <div className="w-full bg-red-500" style={{ height: `${blkPct}%` }} />
                                  <div className="w-full bg-[#D97736]" style={{ height: `${100 - blkPct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between text-[10px] text-[#5C5C5C] mt-1.5">
                          <span>{dossier.timeline[0]?.date?.slice(5)}</span>
                          <span>{dossier.timeline[dossier.timeline.length - 1]?.date?.slice(5)}</span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Withdrawal history */}
              <div>
                <h4 className="text-sm font-medium text-[#121212] mb-2 flex items-center gap-1.5">
                  <ArrowDownToLine className="w-4 h-4 text-[#5C5C5C]" />{isLV ? 'Izmaksu vēsture' : 'Withdrawal history'}
                </h4>
                {dossier.withdrawals.length === 0 ? (
                  <p className="text-xs text-[#5C5C5C]">{isLV ? 'Nav izmaksu.' : 'No withdrawals.'}</p>
                ) : (
                  <div className="space-y-1.5">
                    {dossier.withdrawals.map(wd => (
                      <div key={wd.id} className="flex items-center justify-between rounded-lg border border-[#E5E4E0] px-3 py-2" data-testid={`dossier-withdrawal-${wd.id}`}>
                        <div>
                          <div className="text-sm font-medium text-[#121212]">€{Number(wd.amount || 0).toFixed(2)}</div>
                          <div className="text-[10px] text-[#5C5C5C]">IBAN ...{wd.iban_last4} · {fmtTime(wd.created_at)}</div>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${wd.status === 'rejected' ? 'text-red-600 border-red-200' : wd.status === 'completed' ? 'text-emerald-600 border-emerald-200' : ''}`}>{wd.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Security events */}
              <div>
                <h4 className="text-sm font-medium text-[#121212] mb-2 flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4 text-[#5C5C5C]" />{isLV ? 'Drošības notikumi' : 'Security events'}
                </h4>
                {dossier.events.length === 0 ? (
                  <p className="text-xs text-[#5C5C5C]">{isLV ? 'Nav notikumu.' : 'No events.'}</p>
                ) : (
                  <div className="space-y-1.5">
                    {dossier.events.map(ev => {
                      const m = typeMeta[ev.event_type] || { label: ev.event_type };
                      return (
                        <div key={ev.id} className="flex items-center justify-between rounded-lg border border-[#E5E4E0] px-3 py-2" data-testid={`dossier-event-${ev.id}`}>
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-[#121212]">{m.label}</div>
                            <div className="text-[10px] text-[#5C5C5C]">{detailLabel(ev.detail)}{ev.ip ? ` · ${ev.ip}` : ''}</div>
                          </div>
                          <span className="text-[10px] text-[#5C5C5C] shrink-0">{fmtTime(ev.created_at)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4" data-testid={testid}>
      <div className="text-xs text-[#5C5C5C] mb-1">{label}</div>
      <div className="font-outfit text-2xl font-bold text-[#121212]">{value}</div>
    </div>
  );
}
